/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { defineMessages } from 'react-intl';
import { FormFieldValue, FormValidator } from 'src/core/types/form';
import * as booleanValidators from 'src/core/utils/formValidators/booleanValidators';
import * as fileValidators from 'src/core/utils/formValidators/fileValidators';
import * as numberValidators from 'src/core/utils/formValidators/numberValidators';
import * as stringArrayValidators from 'src/core/utils/formValidators/stringArrayValidators';
import * as stringValidators from 'src/core/utils/formValidators/stringValidators';

const messages = defineMessages({
    errorMessage: {
        id: 'portal.createDuplicateValueValidator.errorMessage',
        defaultMessage: 'Enter a new value'
    }
});

/**
 * Creates a validator that validates whether the given value is a duplicate
 * of the original value or not.
 *
 * @param original The original value that is being tested against.
 * @returns A form validator that validates against the original value.
 */
export const createDuplicateValueValidator = (original: FormFieldValue): FormValidator => (value: FormFieldValue) => {
    let validationResult: boolean;

    if (original === null || original === undefined) {
        return;
    }

    if (value === undefined) {
        return;
    }

    if (typeof value === 'number') {
        validationResult = numberValidators.createDuplicateValueValidator(original)(value);
    }
    if (typeof value === 'boolean') {
        validationResult = booleanValidators.createDuplicateValueValidator(original)(value);
    }
    if (typeof value === 'string') {
        validationResult = stringValidators.createDuplicateValueValidator(original)(value);
    }
    if (value instanceof File) {
        validationResult = fileValidators.createDuplicateValueValidator(original)(value);
    }
    if (Array.isArray(value)) {
        validationResult = stringArrayValidators.createDuplicateValueValidator(original)(value);
    }

    if (!validationResult) {
        return { messageDescriptor: messages.errorMessage };
    }
};
